'use strict';

import analytics from '../../js/analytics';

const POLL_INTERVAL = 500;
const MAX_ATTEMPTS = 10;

const SELECTORS = {
    recommendationSlot: 'div[id^="cq_recomm_slot"]',
    wrapper: '.menu-cross-sell-wrapper',
    loader: '.loading-menu-carousel',
    status: '#menu-carousel-status',
    recommendationContainer:
        '.menu-recommendation-container',
    recommendationCarousel:
        '.menu-recommendation-container .menucross-sell-recommendations'
};

/**
 * Check whether an element contains recommendation results.
 *
 * @param {jQuery} $element
 * @returns {boolean}
 */
const hasResults = ($element) => {
    return (
        $element &&
        $element.length > 0 &&
        $element.children().length > 0
    );
};

/**
 * Keep the complete cross-sell wrapper hidden while
 * recommendations are loading.
 *
 * @param {jQuery} $selectedCategory
 */
const showLoader = ($selectedCategory) => {
    if (!$selectedCategory || !$selectedCategory.length) {
        return;
    }

    const $wrapper = $selectedCategory.find(
        SELECTORS.wrapper
    );

    const $loader = $selectedCategory.find(
        SELECTORS.loader
    );

    $wrapper.hide();
    $loader.show();
};

/**
 * Show the complete cross-sell wrapper.
 *
 * @param {jQuery} $selectedCategory
 */
const showRecommendations = ($selectedCategory) => {
    if (!$selectedCategory || !$selectedCategory.length) {
        return;
    }

    const $wrapper = $selectedCategory.find(
        SELECTORS.wrapper
    );

    const $loader = $selectedCategory.find(
        SELECTORS.loader
    );

    const $status = $selectedCategory.find(
        SELECTORS.status
    );

    $loader.hide();
    $wrapper.show();

    /*
     * Make sure the live region is not display:none.
     *
     * If the status is visually hidden, use an
     * sr-only CSS class instead of display:none.
     */
    $status.show();
};

/**
 * Hide cross-sell completely.
 *
 * @param {jQuery} $selectedCategory
 */
const hideRecommendations = ($selectedCategory) => {
    if (!$selectedCategory || !$selectedCategory.length) {
        return;
    }

    const $wrapper = $selectedCategory.find(
        SELECTORS.wrapper
    );

    const $loader = $selectedCategory.find(
        SELECTORS.loader
    );

    const $status = $selectedCategory.find(
        SELECTORS.status
    );

    $wrapper.hide();
    $loader.hide();
    $status.hide();
};

const disableRecommendationLinks = ($menuCrossSell) => {
    if (
        !$menuCrossSell ||
        !$menuCrossSell.length
    ) {
        return;
    }
    setTimeout(function() {
        $menuCrossSell
        .find(
            'a.product-link, a.thumb-link'
        )
        .attr('tabindex', '-1');
    }, 500);
};

const updateRecommendationAria = ($menuCrossSell) => {
    if (
        !$menuCrossSell ||
        !$menuCrossSell.length
    ) {
        return;
    }

    const $slides = $menuCrossSell.find(
        '.menucross-sell-recommendations-tile'
    );

    const totalSlides = $slides.length;

    if (!totalSlides) {
        return;
    }

    $slides.each(function (index) {
        $(this).attr({
            'aria-setsize': totalSlides,
            'aria-posinset': index + 1
        });
    });
};

/**
 * Generic polling helper.
 *
 * @param {Function} callback
 * @param {Function} onTimeout
 */
const pollUntilReady = (callback, onTimeout) => {
    let attempts = 0;

    const intervalId = setInterval(() => {
        attempts++;

        if (callback()) {
            clearInterval(intervalId);
            return;
        }

        if (attempts >= MAX_ATTEMPTS) {
            clearInterval(intervalId);

            if (typeof onTimeout === 'function') {
                onTimeout();
            }
        }
    }, POLL_INTERVAL);
};

/**
 * Check whether CQ recommendation results are available.
 *
 * @param {jQuery} $selectedCategory
 * @returns {boolean}
 */
const hasCQResults = ($selectedCategory) => {
    const $recommendationSlot = $selectedCategory
        .find(SELECTORS.recommendationSlot)
        .first();

    return hasResults($recommendationSlot);
};

/**
 * Check whether the actual recommendation carousel
 * contains results.
 *
 * @param {jQuery} $selectedCategory
 * @returns {boolean}
 */
const hasCarouselResults = ($selectedCategory) => {
    const $menuCrossSell = $selectedCategory
        .find(SELECTORS.recommendationCarousel)
        .first();

    return hasResults($menuCrossSell);
};

/**
 * Update carousel accessibility status.
 *
 * @param {jQuery} $status
 * @param {Object} slick
 * @param {number} currentSlide
 */
const updateCarouselStatus = (
    $status,
    slick,
    currentSlide = 0
) => {
    if (
        !$status ||
        !$status.length ||
        !slick
    ) {
        return;
    }

    const visibleSlides = parseInt(
        slick.options.slidesToShow,
        10
    );

    const totalSlides = slick.slideCount;

    if (
        !visibleSlides ||
        !totalSlides
    ) {
        return;
    }

    const start =
        currentSlide + 1;

    const end = Math.min(
        start + visibleSlides - 1,
        totalSlides
    );

    const message =
        `Showing items ${start} to ${end} of ${totalSlides}.`;

    /*
     * Make sure the status element is available
     * to assistive technology.
     */
    $status.show();

    /*
     * Update the live region directly.
     *
     * Do not clear it first because that can make
     * screen reader announcements unreliable.
     */
    $status.text(message);
};

/**
 * Initialize Slick carousel.
 *
 * The wrapper stays hidden during Slick initialization.
 *
 * @param {jQuery} $selectedCategory
 */
const menuCrossSellRecommendations = ($selectedCategory) => {
    if (!$selectedCategory || !$selectedCategory.length) {
        return;
    }

    const $menuCrossSell = $selectedCategory
        .find(SELECTORS.recommendationCarousel)
        .first();

    if (!$menuCrossSell.length) {
        return;
    }

    const $wrapper = $selectedCategory.find(
        SELECTORS.wrapper
    );

    const $loader = $selectedCategory.find(
        SELECTORS.loader
    );

    const $status = $selectedCategory.find(
        SELECTORS.status
    );

    const selectedItem = $selectedCategory
        .find('.level-1-link')
        .text();

    const ariaLabel =
        Resources.NAV_MENU_LABEL + ' ' + selectedItem;

    $wrapper.attr(
        'aria-label',
        ariaLabel
    );

    /*
     * IMPORTANT:
     *
     * Keep the complete wrapper hidden until Slick
     * has finished initializing.
     */
    $wrapper.hide();
    $loader.show();

    /*
     * If Slick has already been initialized for this
     * particular carousel, don't initialize it again.
     *
     * Do NOT call getSlick() here.
     */
    if ($menuCrossSell.hasClass('slick-initialized')) {
        showRecommendations(
            $selectedCategory
        );

        return;
    }

    /*
     * Don't initialize Slick without results.
     */
    if (!$menuCrossSell.children().length) {
        hideRecommendations(
            $selectedCategory
        );

        return;
    }

    let userInteracted = false;

    /*
     * Remove handlers belonging to this carousel only.
     */
    $menuCrossSell.off('.menuCrossSell');

    $selectedCategory
        .find(SELECTORS.recommendationContainer)
        .off('.menuCrossSell');

    /*
     * Slick event handlers.
     *
     * IMPORTANT:
     *
     * These handlers MUST be registered before
     * calling $menuCrossSell.slick().
     */
    $menuCrossSell
        .addClass('first-slide')

        /*
         * Slick has finished initialization.
         *
         * Slick provides the instance as the second
         * argument: function(event, slick).
         */
        .on(
            'init.menuCrossSell',
            function (event, slick) {
                $menuCrossSell.addClass(
                    'first-slide'
                );

                /*
                 * Slick is now initialized.
                 *
                 * Show wrapper and hide loader.
                 */
                showRecommendations(
                    $selectedCategory
                );
                updateRecommendationAria(
                    $menuCrossSell
                );
                disableRecommendationLinks(
                    $menuCrossSell
                );
                /*
                 * IMPORTANT:
                 *
                 * Use the slick instance supplied
                 * by the init event.
                 *
                 * Do NOT use:
                 *
                 * $menuCrossSell.slick('getSlick')
                 */
                updateCarouselStatus(
                    $status,
                    slick,
                    0
                );

                /*
                 * Slick was initialized while the wrapper
                 * was display:none.
                 *
                 * Recalculate its dimensions after the
                 * wrapper becomes visible.
                 */
                setTimeout(() => {
                    if (
                        $menuCrossSell.hasClass(
                            'slick-initialized'
                        )
                    ) {
                        $menuCrossSell.slick(
                            'setPosition'
                        );
                        disableRecommendationLinks(
                            $menuCrossSell
                        );
                    }
                }, 10);

                analytics.menuCrosssellList(
                    $menuCrossSell
                );
            }
        )

        /*
         * Track slide changes caused by interaction.
         */
        .on(
            'beforeChange.menuCrossSell',
            function () {
                userInteracted = true;
            }
        )

        /*
         * Update status after the user moves
         * the carousel.
         */
        .on(
            'afterChange.menuCrossSell',
            function (
                event,
                slick,
                currentSlide
            ) {
                if (currentSlide === 0) {
                    $menuCrossSell.addClass(
                        'first-slide'
                    );
                } else {
                    $menuCrossSell.removeClass(
                        'first-slide'
                    );
                }

                /*
                 * Do not announce again during the
                 * initial Slick setup.
                 */
                if (!userInteracted) {
                    return;
                }
                disableRecommendationLinks(
                    $menuCrossSell
                );
                updateCarouselStatus(
                    $status,
                    slick,
                    currentSlide
                );

                userInteracted = false;
            }
        )

        /*
         * Track swipe interaction.
         */
        .on(
            'swipe.menuCrossSell',
            function () {
                userInteracted = true;
            }
        );

    /*
     * Arrow interactions.
     */
    $selectedCategory
        .find(SELECTORS.recommendationContainer)
        .on(
            'click.menuCrossSell',
            '.slick-next, .slick-prev',
            function () {
                userInteracted = true;
            }
        );

    /*
     * Initialize Slick while the entire wrapper
     * is hidden.
     *
     * The init event above is already registered,
     * so it will receive the Slick instance.
     */
    $menuCrossSell.slick({
        initialSlide: 0,
        autoplay: false,
        dots: false,
        arrows: true,
        swipe: true,
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        variableWidth: true,
        swipeToSlide: false,
        slidesToScroll: 3,
        fade: false,
        cssEase: 'linear',

        nextArrow:
            '<button type="button" tabindex="0" class="slick-next" aria-label="' +
            Resources.NAV_MENU_NEXT +
            '"><i class="icon icon-rightCarrot"></i></button>',

        prevArrow:
            '<button type="button" tabindex="0" class="slick-prev" aria-label="' +
            Resources.NAV_MENU_PREV +
            '"><i class="icon icon-leftCarrot"></i></button>',

        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    swipeToSlide: 1
                }
            }
        ]
    });

    analytics.menuCrossSellClick(
        $menuCrossSell
    );

    /*
     * Final position update after the wrapper has
     * become visible.
     */
    setTimeout(() => {
        if (
            $menuCrossSell.hasClass(
                'slick-initialized'
            )
        ) {
            $menuCrossSell.slick(
                'setPosition'
            );
            disableRecommendationLinks(
                $menuCrossSell
            );
        } else {
            $status.remove();
        }
    }, 20);
};

/**
 * Copy recommendation slots from desktop navigation
 * to mobile navigation.
 *
 * @returns {void}
 */
const copyRecommendationSlots = () => {
    $('.nav-menu ul.level-1 > li').each(function () {
        const $desktopCategory = $(this);

        const $desktopSlot = $desktopCategory
            .find(
                SELECTORS.recommendationSlot
            )
            .first();

        if (!$desktopSlot.length) {
            return;
        }

        const dataAttr =
            $desktopCategory.attr(
                'data-attr'
            );

        const $mobileCategory = $(
            '.mobile-navigation ul.level-1 > li[data-attr="' +
            dataAttr +
            '"]'
        );

        const $mobileSlot = $mobileCategory
            .find(
                SELECTORS.recommendationSlot
            )
            .first();

        if (!$mobileSlot.length) {
            return;
        }

        const desktopHtml =
            $desktopSlot.html();

        if (
            desktopHtml &&
            $mobileSlot.html() !== desktopHtml
        ) {
            $mobileSlot.html(
                desktopHtml
            );
        }
    });

    /*
     * Once the slots have been copied, handle
     * the active mobile category.
     */
    initMobileCrossSell();
};

/**
 * Initialize mobile cross-sell.
 *
 * @returns {void}
 */
const initMobileCrossSell = () => {
    const $selectedCategory =
        $('.mobile-navigation ul.level-1 > li.active');

    if (!$selectedCategory.length) {
        return;
    }

    /*
     * Loader was copied from desktop along with
     * the recommendation slot.
     *
     * Show it immediately.
     */
    showLoader(
        $selectedCategory
    );

    pollUntilReady(
        () => {
            /*
             * Wait for the actual mobile carousel
             * to contain recommendation items.
             */
            if (
                !hasCarouselResults(
                    $selectedCategory
                )
            ) {
                return false;
            }

            /*
             * Carousel is ready.
             */
            menuCrossSellRecommendations(
                $selectedCategory
            );

            return true;
        },
        () => {
            hideRecommendations(
                $selectedCategory
            );
        }
    );
};

/**
 * Initialize desktop cross-sell.
 *
 * @returns {void}
 */
const initCrossSell = () => {
    pollUntilReady(
        () => {
            /*
             * Get the current hover category.
             */
            const $selectedCategory =
                $('ul.level-1 > li.hover');

            if (!$selectedCategory.length) {
                return false;
            }

            /*
             * Always keep wrapper hidden until
             * Slick is ready.
             */
            showLoader(
                $selectedCategory
            );

            /*
             * Wait for CQ recommendation results.
             */
            if (
                !hasCQResults(
                    $selectedCategory
                )
            ) {
                return false;
            }

            /*
             * Wait for actual carousel results.
             */
            if (
                !hasCarouselResults(
                    $selectedCategory
                )
            ) {
                return false;
            }

            /*
             * Everything is ready.
             *
             * Initialize Slick while wrapper
             * remains hidden.
             */
            menuCrossSellRecommendations(
                $selectedCategory
            );

            return true;
        },
        () => {
            const $selectedCategory =
                $('ul.level-1 > li.hover');

            hideRecommendations(
                $selectedCategory
            );
        }
    );
};

/**
 * Initialize mobile cross-sell after window load.
 */
$(window).on('load', function () {
    if ($(window).width() <= 768) {
        copyRecommendationSlots();
    }
});

/**
 * Initialize Mini cross sell.
 */
export default {
    init: function () {
        if ($(window).width() <= 768) {
            copyRecommendationSlots();
            return;
        }

        initCrossSell();
    }
};